'use client';

import type React from 'react';

import { useState } from 'react';
import { Search, ArrowRight, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { useRouter } from 'next/navigation';

export default function ChatInput() {
  const router = useRouter();
  const [query, setQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const exampleQueries = [
    "What is NVIDIA's GPU market share and who are its main competitors?",
    "What was Amazon's AWS revenue over the last 6 quarters?",
    "How has Microsoft's cloud revenue grown over the past year?",
    "What are the highlights from Apple's latest earnings report?",
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedQuery = query.trim();
    if (!trimmedQuery || isLoading) return;

    setIsLoading(true);
    router.push(`/chat?search=${encodeURIComponent(trimmedQuery)}`);
  };

  const handleExampleClick = (example: string) => {
    setQuery(example);
  };

  return (
    <div className="w-full max-w-4xl mx-auto px-4 md:px-0">
      <form onSubmit={handleSubmit} className="relative">
        <div className="flex items-center relative">
          <Search
            className="absolute left-5 text-zinc-400 dark:text-zinc-500"
            size={20}
          />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Ask anything about stocks..."
            disabled={isLoading}
            className="w-full h-14 md:h-16 pl-12 md:pl-14 pr-14 md:pr-16 rounded-xl md:rounded-2xl border border-zinc-200 dark:border-zinc-700/50 bg-white dark:bg-zinc-900/70 text-zinc-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#4d2ef5]/50 dark:focus:ring-[#4d2ef5]/50 shadow-sm hover:shadow-md focus:shadow-md transition-all text-sm md:text-base disabled:opacity-70"
          />
          <motion.button
            type="submit"
            disabled={isLoading || !query.trim()}
            className="absolute right-3 size-8 md:size-10 rounded-lg md:rounded-xl bg-[#4d2ef5] dark:bg-[#4d2ef5] text-white flex items-center justify-center hover:bg-[#3f25cc] dark:hover:bg-[#3f25cc] disabled:bg-[#6b52e3] disabled:cursor-not-allowed"
            whileHover={{ scale: isLoading ? 1 : 1.05 }}
            whileTap={{ scale: isLoading ? 1 : 0.95 }}
          >
            {isLoading ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <ArrowRight size={16} />
            )}
          </motion.button>
        </div>
      </form>

      <div className="mt-3 md:mt-4 grid grid-cols-1 gap-2 md:grid-cols-2 md:gap-3">
        {exampleQueries.map((example, index) => (
          <motion.button
            key={index}
            onClick={() => handleExampleClick(example)}
            disabled={isLoading}
            className="text-left p-3 md:p-4 rounded-lg md:rounded-xl border border-zinc-200 dark:border-zinc-800/50 bg-white/80 dark:bg-zinc-900/50 hover:bg-zinc-50 dark:hover:bg-zinc-800/80 text-zinc-600 dark:text-zinc-300 text-xs md:text-sm backdrop-blur-sm disabled:opacity-70 disabled:cursor-not-allowed"
            whileHover={{
              y: isLoading ? 0 : -2,
              boxShadow: isLoading ? 'none' : '0 4px 12px rgba(0,0,0,0.05)',
            }}
            whileTap={{ y: 0 }}
          >
            {example}
          </motion.button>
        ))}
      </div>

      {isLoading && (
        <div className="mt-4 text-center text-sm text-zinc-500 dark:text-zinc-400 animate-pulse">
          Searching financial data...
        </div>
      )}
    </div>
  );
}
